import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { MaskLines, Spark, ease } from "./Primitives";
import { useT } from "@/lib/i18n";
import { scrollToId } from "@/lib/scroll";

const Ring = ({ text }) => (
  <motion.div
    className="absolute -left-10 top-24 md:-left-14 md:top-28 w-28 h-28 md:w-36 md:h-36 rounded-full bg-[#0c0a09] border border-[var(--line)] grid place-items-center z-20"
    animate={{ rotate: 360 }}
    transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
    data-testid="hero-ring-badge"
  >
    <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full p-1">
      <defs><path id="circ" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
      <text fontSize="7.2" fill="#e6c594" fontFamily="JetBrains Mono, monospace">
        <textPath href="#circ" textLength="236" lengthAdjust="spacing">{text}</textPath>
      </text>
    </svg>
    <Spark className="w-5 h-5 text-[var(--gold)]" />
  </motion.div>
);

const Portrait = ({ ring }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 800], [0, 30]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div className="relative [perspective:1200px]" onMouseMove={onMove} onMouseLeave={reset} ref={ref}>
      <motion.div
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 24px 24px)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 24px 24px)" }}
        transition={{ duration: 1.6, delay: 0.5, ease }}
        className="relative aspect-[4/5] w-full overflow-hidden bg-[#1c1917] spotlight"
        data-testid="hero-portrait"
      >
        <motion.img
          src="/lene.jpg"
          alt="Lene Mauro, Social Media Manager"
          style={{ y: imgY, scale: 1.06, transformOrigin: "50% 100%" }}
          className="absolute inset-0 w-full h-full object-cover object-[56%_0%] portrait-tone"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0c0a09]/70 via-transparent to-transparent" />
        <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between" style={{ transform: "translateZ(40px)" }}>
          <div>
            <p className="font-display text-2xl md:text-3xl">Lene Mauro</p>
            <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[var(--gold)]">Social · Web</p>
          </div>
          <span className="font-mono text-xs text-stone-300">©2026</span>
        </div>
      </motion.div>
      <motion.div initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 1.5, ease }}>
        <Ring text={ring} />
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const t = useT().hero;
  return (
    <section id="top" className="relative min-h-[100svh] pt-28 md:pt-32 pb-16 overflow-hidden">
      <div className="hero-glow" />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7 relative z-10">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3, ease }}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--line)] px-4 py-2 mb-10 text-xs text-stone-300" data-testid="hero-availability-badge">
            <span className="relative flex w-2 h-2"><span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" /><span className="relative w-2 h-2 rounded-full bg-emerald-400" /></span>
            {t.badge}
          </motion.div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 1 }} className="overline mb-6">{t.over}</motion.p>
          <h1 className="font-display font-light leading-[0.98] tracking-[-0.02em] text-[13vw] sm:text-7xl lg:text-[6.6rem]" data-testid="hero-title">
            <MaskLines lines={t.lines} italicIndex={t.italic} delay={0.45} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9, ease }}
            className="mt-8 max-w-xl text-base md:text-lg text-stone-300 leading-relaxed">
            {t.sub}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.9, ease }} className="mt-10 flex flex-wrap gap-4">
            <button data-testid="hero-cta-primary" onClick={() => scrollToId("contact")} className="btn-gold group">
              {t.cta1} <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button data-testid="hero-cta-secondary" onClick={() => scrollToId("pricing")} className="btn-ghost">
              {t.cta2}
            </button>
          </motion.div>
        </div>
        <div className="lg:col-span-5 relative w-full max-w-[460px] mx-auto lg:ml-auto pl-8 md:pl-0">
          <Portrait ring={t.ring} />
        </div>
      </div>
      <button onClick={() => scrollToId("about")} data-testid="hero-scroll-btn"
        className="hidden md:flex absolute bottom-8 left-10 items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-stone-400 hover:text-[var(--gold)] transition-colors">
        <ArrowDownRight size={16} className="animate-bounce" /> {t.scroll}
      </button>
    </section>
  );
};
